// Text helpers for the store search: accent-insensitive tokens, the words customers use for
// each product family (synonyms) and a small typo tolerance.

// 'Guantillas Montecci' -> 'guantillas montecci'; 'E-MTB' -> 'emtb'; 'niños' -> 'ninos'
export function fold(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\be-(?=[a-z])/g, 'e')
        .replace(/ebikes?\b/g, 'ebike');
}

// 'Herramientas y Aceites' -> 'herramientas-y-aceites'; 'Puños' -> 'punos' (keeps every letter, unlike utils.slugify)
export function slug(value) {
    return fold(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// 'cascos' -> 'casco', 'niñas' -> 'nina': singular and plural are the same word for a customer
const stem = (t) => (t.length >= 4 && /[a-z]s$/.test(t) ? t.slice(0, -1) : t);

// Words and numbers ('27.5' stays one token); everything else is a separator
export function tokenize(value) {
    return fold(value)
        .split(/[^a-z0-9.]+/)
        .map((t) => stem(t.replace(/^\.+|\.+$/g, '')))
        .filter((t) => t.length > 0);
}

// Words that mean the same thing for a customer. A query word matches any word of its group.
const SYNONYM_GROUPS = [
    ['bici', 'bicis', 'bicicleta', 'bicicletas', 'bike', 'bikes', 'cicla'],
    ['ebike', 'electrica', 'electricas', 'electrico', 'electricos', 'electric', 'pedelec', 'asistida', 'motor'],
    ['analoga', 'analogas', 'analogo', 'analogos', 'convencional', 'convencionales', 'mecanica', 'mecanicas', 'tradicional', 'tradicionales'],
    ['mtb', 'montana', 'montanera', 'mountain', 'montanesa'],
    ['ruta', 'road', 'carretera', 'asfalto', 'rutera'],
    ['gravel', 'grava', 'todoterreno'],
    ['trekking', 'paseo', 'hibrida', 'urbana', 'ciudad', 'city', 'commuter'],
    ['nino', 'ninos', 'nina', 'ninas', 'kids', 'kid', 'infantil', 'junior', 'pequeno', 'pequenos', 'chiquito', 'chiquita'],
    ['casco', 'cascos', 'helmet', 'helmets'],
    ['guante', 'guantes', 'guantilla', 'guantillas', 'gloves', 'glove'],
    ['puno', 'punos', 'grip', 'grips', 'manilla', 'manillas', 'mango', 'mangos', 'agarre', 'agarres'],
    ['luz', 'luces', 'linterna', 'linternas', 'foco', 'focos', 'lampara', 'lamparas'],
    ['pedal', 'pedales', 'pedals', 'chocle', 'chocles', 'clip', 'spd'],
    ['cadena', 'cadenas', 'chain', 'chains'],
    ['bomba', 'bombas', 'inflador', 'infladores', 'pump', 'co2', 'aire'],
    ['gafa', 'gafas', 'lente', 'lentes', 'goggle', 'goggles', 'glasses', 'espejuelos', 'sol'],
    ['jersey', 'jerseys', 'camiseta', 'camisetas', 'franela', 'franelas', 'polo', 'camisa', 'ropa'],
    ['licra', 'licras', 'lycra', 'pantalon', 'pantalones', 'short', 'shorts', 'bermuda', 'bermudas', 'ropa'],
    ['mochila', 'mochilas', 'bultico', 'bulticos', 'bulto', 'bolso', 'bolsos', 'bag', 'backpack', 'morral'],
    ['gps', 'reloj', 'relojes', 'ciclocomputador', 'ciclocomputadora', 'ciclocomputadoras', 'computadora', 'garmin', 'smartwatch', 'velocimetro'],
    ['herramienta', 'herramientas', 'tool', 'tools', 'multiherramienta', 'llave', 'llaves', 'kit'],
    ['aceite', 'aceites', 'lubricante', 'lubricantes', 'lube', 'grasa', 'limpiador'],
    ['termo', 'termos', 'termera', 'termeras', 'botella', 'botellas', 'bottle', 'caramanola', 'caramanolas', 'portabotella'],
    ['nutricion', 'gel', 'geles', 'energetico', 'energeticos', 'suplemento', 'suplementos', 'hidratacion'],
    ['proteccion', 'protecciones', 'rodillera', 'rodilleras', 'codera', 'coderas', 'protector', 'protectores'],
    ['navidad', 'navidena', 'navideno', 'navidenas', 'regalo', 'regalos', 'reyes', 'nochebuena', 'navideña'],
    ['raymon', 'raimon', 'reymon', 'rayman'],
    ['aro', 'aros', 'rueda', 'ruedas', 'rin', 'rines', 'wheel', 'llanta'],
    ['carbono', 'carbon', 'carbonfiber'],
    ['aluminio', 'alu', 'alloy', 'aluminum', 'aluminium'],
    ['doble', 'full', 'fullsuspension'],
    ['bmx', 'freestyle'],
    ['balance', 'walker', 'equilibrio', 'impulso'],
    ['silla', 'sillin', 'sillines', 'asiento', 'asientos', 'saddle', 'montura'],
    ['zapato', 'zapatos', 'zapatilla', 'zapatillas', 'tenis', 'shoes'],
    ['candado', 'candados', 'lock', 'cadena'],
    ['tenedor', 'horquilla', 'fork', 'suspension'],
    ['freno', 'frenos', 'brake', 'brakes', 'pastilla', 'pastillas'],
    ['goma', 'gomas', 'neumatico', 'neumaticos', 'llanta', 'llantas', 'tire', 'tires', 'cubierta', 'cubiertas'],
    ['oferta', 'ofertas', 'barata', 'barato', 'economica', 'economico', 'descuento', 'especial']
];

const SYNONYMS = new Map();
SYNONYM_GROUPS.forEach((group) => {
    const words = group.map((w) => stem(fold(w)));
    words.forEach((word) => {
        const set = SYNONYMS.get(word) || new Set();
        words.forEach((w) => set.add(w));
        SYNONYMS.set(word, set);
    });
});

// Every spelling a query word can match: itself plus its synonyms
export function alternatives(token) {
    const set = SYNONYMS.get(token);
    return set ? [...set] : [token];
}

// Bounded Levenshtein distance; stops early once it is clearly above `max`
function editDistance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const row = [i];
        let best = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
            if (row[j] < best) best = row[j];
        }
        if (best > max) return max + 1;
        prev = row;
    }
    return prev[b.length];
}

// How well one query word matches one indexed word: 3 exact, 2 prefix, 1 close spelling, 0 none
export function wordMatch(query, word) {
    if (word === query) return 3;
    if (query.length >= 2 && word.startsWith(query)) return 2;
    if (query.length < 4 || /^\d/.test(query)) return 0;
    const max = query.length >= 8 ? 2 : 1;
    return editDistance(query, word, max) <= max ? 1 : 0;
}
